import { createApi, FetchArgs } from '@reduxjs/toolkit/query/react';
import { handleQueryStarted } from '@senderra/shared/baseQuery/handleQueryStarted';
import {
    DispensesResponse,
    DrugShipmentHistoryItemsRequest,
    DrugShipmentHistoryItemsResponse,
    ReferralResponse,
    RefillResponse,
    RXStatusesResponse,
} from './types';
import baseQuery from '@senderra/shared/baseQuery/baseReportsQuery';

export interface ReportsContentProps {
    pageNumber: number;
    pageSize: number;
    ndcNumbers?: string[];
    search?: string;
    statusReason?: string;
    onlyMyPreferences: boolean;
    orderBy?: string;
    orderByAscending?: boolean;
    statuses?: string[];
    statusIds?: number[];
    drugShortNames?: string[];
    doctorStates?: string[];
}

interface ReportsDispensesProps extends Omit<ReportsContentProps, 'statuses'> {}

const REPORTS_API = 'api/v1/reports';

type EndpointName = 'refill' | 'referral' | 'dispenses';

const abortControllers: Record<EndpointName, AbortController> = {
    refill: new AbortController(),
    referral: new AbortController(),
    dispenses: new AbortController(),
};

export const reportsApi = createApi({
    reducerPath: 'reportsApi',
    baseQuery: async (args: FetchArgs, api, extraOptions) => {
        const endpointName = args.url.split('/').pop() as EndpointName;

        if (abortControllers[endpointName]) {
            abortControllers[endpointName].abort();
        }

        abortControllers[endpointName] = new AbortController();

        return baseQuery({ ...args, signal: abortControllers[endpointName].signal }, api, extraOptions);
    },
    tagTypes: ['Reports'],
    endpoints: (builder) => ({
        getReportsRefill: builder.query<RefillResponse, ReportsContentProps>({
            query: (content) => ({ url: `${REPORTS_API}/refill`, method: 'POST', body: content }),
            keepUnusedDataFor: 0,
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await handleQueryStarted(_, { dispatch, queryFulfilled });
            },
            providesTags: ['Reports'],
        }),
        getReportsReferral: builder.query<ReferralResponse, ReportsContentProps>({
            query: (content) => ({ url: `${REPORTS_API}/referral`, method: 'POST', body: content }),
            keepUnusedDataFor: 0,
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await handleQueryStarted(_, { dispatch, queryFulfilled });
            },
            providesTags: ['Reports'],
        }),
        getReportsDispenses: builder.query<DispensesResponse, ReportsDispensesProps>({
            query: (content) => ({ url: `${REPORTS_API}/dispenses`, method: 'POST', body: content }),
            keepUnusedDataFor: 0,
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await handleQueryStarted(_, { dispatch, queryFulfilled });
            },
            providesTags: ['Reports'],
        }),
        getRxStatuses: builder.query<RXStatusesResponse, void>({
            query: () => ({ url: `${REPORTS_API}/rxStatuses`, method: 'GET' }),
            providesTags: ['Reports'],
        }),
        getDrugShipmentHistory: builder.query<DrugShipmentHistoryItemsResponse, DrugShipmentHistoryItemsRequest>({
            query: (params) => ({
                url: `${REPORTS_API}/drugShipmentHistoryItems`,
                method: 'GET',
                params: {
                    DrugShortName: params.drugShortName,
                    PatientId: params.patientId,
                    PageNumber: params.pageNumber,
                    PageSize: params.pageSize,
                    OrderByAscending: params.orderByAscending,
                },
            }),
            providesTags: ['Reports'],
        }),
    }),
});

export const {
    useGetReportsRefillQuery,
    useGetReportsReferralQuery,
    useGetReportsDispensesQuery,
    useGetRxStatusesQuery,
    useGetDrugShipmentHistoryQuery,
} = reportsApi;
